<template>
  <div class="i-select" ref="container">
    <div
      ref="trigger"
      class="is-trigger"
      :class="{
        'is-open': isOpen,
        'is-disabled': disabled,
        'is-error': error,
        'is-has-value': !!modelValue
      }"
      @click="toggle"
    >
      <div class="is-value-area">
        <span v-if="modelValue !== '' && modelValue !== null && modelValue !== undefined" class="is-value">{{ selectedLabel }}</span>
        <span v-else class="is-placeholder">{{ placeholder }}</span>
      </div>
      <div class="is-arrow">
        <svg
          class="is-arrow-icon"
          :class="{ 'is-arrow-open': isOpen }"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-show="isOpen"
        ref="dropdown"
        class="is-dropdown"
        :style="dropdownStyle"
        @click.stop
      >
        <div class="is-options-list">
          <div
            v-for="opt in normalizedOptions"
            :key="opt.value"
            class="is-option"
            :class="{ 'is-option-selected': String(modelValue) === String(opt.value) }"
            @click.stop="selectOption(opt.value)"
          >
            {{ opt.label }}
          </div>
          <div v-if="normalizedOptions.length === 0" class="is-empty">
            Nenhuma opção
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';

export default {
  name: 'ISelect',
  props: {
    modelValue: { type: [String, Number, null], default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Selecione' },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const isOpen = ref(false);
    const container = ref(null);
    const trigger = ref(null);
    const dropdown = ref(null);
    const dropdownStyle = ref({});

    const normalizedOptions = computed(() => {
      return props.options.map(opt => {
        if (typeof opt === 'object' && opt !== null) {
          return { label: opt.label || opt.name || opt.text || String(opt.value), value: opt.value ?? opt.id ?? '' };
        }
        return { label: String(opt), value: opt };
      });
    });

    const selectedLabel = computed(() => {
      const found = normalizedOptions.value.find(o => String(o.value) === String(props.modelValue));
      return found ? found.label : props.modelValue;
    });

    function positionDropdown() {
      if (!trigger.value) return;
      const rect = trigger.value.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openAbove = spaceBelow < 260 && rect.top > 260;

      dropdownStyle.value = {
        position: 'fixed',
        width: rect.width + 'px',
        left: rect.left + 'px',
        zIndex: 99999,
        ...(openAbove
          ? { bottom: (window.innerHeight - rect.top + 4) + 'px' }
          : { top: (rect.bottom + 4) + 'px' }
        )
      };
    }

    function toggle() {
      if (props.disabled) return;
      isOpen.value = !isOpen.value;
      if (isOpen.value) {
        nextTick(positionDropdown);
      }
    }

    function selectOption(value) {
      emit('update:modelValue', value);
      emit('change', value);
      isOpen.value = false;
    }

    function onClickOutside(e) {
      if (container.value && !container.value.contains(e.target) &&
          dropdown.value && !dropdown.value.contains(e.target)) {
        isOpen.value = false;
      }
    }

    function onScroll() {
      if (isOpen.value) positionDropdown();
    }

    onMounted(() => {
      document.addEventListener('click', onClickOutside);
      window.addEventListener('scroll', onScroll, true);
      window.addEventListener('resize', onScroll);
    });

    onBeforeUnmount(() => {
      document.removeEventListener('click', onClickOutside);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    });

    return {
      isOpen, container, trigger, dropdown, dropdownStyle,
      normalizedOptions, selectedLabel, toggle, selectOption
    };
  }
};
</script>

<style scoped>
.i-select {
  position: relative;
  width: 100%;
}

.is-trigger {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 54px;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.is-trigger:hover { border-color: #9ca3af; }
.is-trigger.is-open { border-color: #9ca3af; box-shadow: 0 0 0 1px #e5e7eb; }
.is-trigger.is-disabled { opacity: 0.7; cursor: not-allowed; background: #f3f4f6; }
.is-trigger.is-error { border-color: #ef4444 !important; }

.is-value-area {
  flex: 1;
  display: flex;
  align-items: center;
  min-height: 38px;
  padding: 0.5rem 0.75rem;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.is-value { font-size: 0.875rem; color: #111827; }
.is-placeholder { font-size: 0.875rem; color: #9ca3af; }

.is-arrow {
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  color: #6b7280;
}

.is-arrow-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.2s;
}

.is-arrow-open {
  transform: rotate(180deg);
}
</style>

<style>
/* Dropdown (global — teleportado ao body) */
.is-dropdown {
  background: #fff;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1);
}

.is-options-list {
  max-height: 250px;
  overflow-y: auto;
  padding: 0.25rem 0;
}

.is-option {
  padding: 0.6rem 0.75rem;
  cursor: pointer;
  font-size: 0.875rem;
  color: #111827;
  transition: background 0.1s;
}

.is-option:hover {
  background: #f3f4f6;
}

.is-option-selected {
  background: #eff6ff;
  font-weight: 500;
}

.is-option-selected:hover {
  background: #dbeafe;
}

.is-empty {
  padding: 0.75rem;
  text-align: center;
  font-size: 0.8rem;
  color: #9ca3af;
}
</style>
